import type {
  LinkPurpose,
  LinkReportReason,
  LinkSessionInfo,
  LinkStep,
} from '@gradex-hire/contracts';
import { Suspense, useEffect, useId, useRef, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { NoCodeHelp } from '../../ui/components/NoCodeHelp.js';

import { AuthLayout } from '../../app/layouts/AuthLayout.js';
import { ApiError } from '../../lib/api/errors.js';
import { setLinkScope } from '../../lib/api/link-scope.js';
import '../../lib/i18n/links-strings.js';
import { PageHeader } from '../../ui/components/PageHeader.js';
import { CodeEntry } from '../sign-in/CodeEntry.js';
import {
  endLinkSession,
  linkSession,
  openLink,
  reportLink,
  sendLinkCode,
  verifyLink,
} from './api.js';
import { captureSecret, forgetSecret } from './fragment.js';
import { FULL_PAGE_OUTLETS, PURPOSE_OUTLETS } from './outlets.js';
import '../../ui/components/button.css';
import '../../ui/components/field.css';
import '../sign-in/sign-in.css';
import './secure-link.css';

type State =
  | { kind: 'opening' }
  | { kind: 'inactive' }
  | { kind: 'unavailable' }
  | {
      kind: 'verify';
      purpose: LinkPurpose;
      maskedEmail: string;
      codeSent: boolean;
      person: string | null;
    }
  | { kind: 'ready'; session: LinkSessionInfo }
  | { kind: 'done' };

/** Each link is opened once per page load, even when React mounts the page twice (StrictMode). */
const opened = new Map<string, Promise<LinkStep>>();

const isRefusal = (error: unknown) =>
  error instanceof ApiError && error.isClientError && error.status !== 429;

/** The page's header for robots and referrers (the server sends both too). */
function useCapabilityMeta(): void {
  useEffect(() => {
    const added = [
      ['referrer', 'no-referrer'],
      ['robots', 'noindex, nofollow'],
    ].map(([name = '', content = '']) => {
      const meta = document.createElement('meta');
      meta.name = name;
      meta.content = content;
      document.head.append(meta);
      return meta;
    });
    return () => {
      for (const meta of added) meta.remove();
    };
  }, []);
}

/**
 * The landing page of every secure link (`/l/:linkId#secret`, in the public
 * bundle). It takes the secret out of the address bar at once, then renders
 * nothing about the link until `open` succeeds: a code step for a link bound
 * to an address, then the purpose's own screens (the purpose outlet). Every
 * refusal is the same neutral "This link isn't active". "Report this link"
 * is always offered.
 */
export function LinkPage({ linkId }: { linkId: string }) {
  const { t } = useTranslation();
  useCapabilityMeta();
  const [secret] = useState(() => captureSecret(linkId));
  // Every link-session call from this page is for this link only.
  useEffect(() => {
    setLinkScope(linkId);
    return () => {
      setLinkScope(null);
    };
  }, [linkId]);
  const [state, setState] = useState<State>(() =>
    secret ? { kind: 'opening' } : { kind: 'inactive' },
  );

  const ready = async () => {
    setState({ kind: 'ready', session: await linkSession() });
  };

  useEffect(() => {
    if (!secret) return;
    let attempt = opened.get(linkId);
    if (!attempt) {
      attempt = openLink(linkId, secret);
      opened.set(linkId, attempt);
    }
    let active = true;
    attempt
      .then(async (step) => {
        if (!active) return;
        if (step.step === 'verify') {
          setState({
            kind: 'verify',
            purpose: step.purpose,
            maskedEmail: step.maskedEmail,
            codeSent: step.codeSent,
            person: step.welcome?.person ?? null,
          });
          return;
        }
        await ready();
      })
      .catch((failure: unknown) => {
        if (!active) return;
        opened.delete(linkId);
        if (isRefusal(failure)) {
          forgetSecret(linkId);
          setState({ kind: 'inactive' });
        } else {
          setState({ kind: 'unavailable' });
        }
      });
    return () => {
      active = false;
    };
    // `ready` only sets state.
  }, [linkId, secret]);

  const done = () => {
    void endLinkSession().catch(() => undefined);
    forgetSecret(linkId);
    setState({ kind: 'done' });
  };

  const Outlet = state.kind === 'ready' ? PURPOSE_OUTLETS[state.session.purpose] : undefined;

  // A purpose with a page of its own (a reference request) takes over from the
  // code step on, outside the sign-in layout.
  const purpose =
    state.kind === 'verify' ? state.purpose : state.kind === 'ready' ? state.session.purpose : null;
  const FullPage = purpose ? FULL_PAGE_OUTLETS[purpose] : undefined;
  if (FullPage && secret && (state.kind === 'verify' || state.kind === 'ready')) {
    return (
      <Suspense
        fallback={
          <p className="sign-in__status" role="status">
            {t('links.opening')}
          </p>
        }
      >
        <FullPage
          linkId={linkId}
          secret={secret}
          maskedEmail={state.kind === 'verify' ? state.maskedEmail : ''}
          codeSent={state.kind === 'verify' && state.codeSent}
          person={state.kind === 'verify' ? (state.person ?? '') : ''}
          onDone={done}
          onRefused={() => {
            forgetSecret(linkId);
            setState({ kind: 'inactive' });
          }}
          report={<ReportLink linkId={linkId} />}
        />
      </Suspense>
    );
  }

  return (
    <AuthLayout>
      <div className="sign-in secure-link">
        {state.kind === 'opening' ? (
          <>
            <PageHeader title={t('links.title')} />
            <p className="sign-in__status" role="status">
              {t('links.opening')}
            </p>
          </>
        ) : state.kind === 'inactive' || (state.kind === 'ready' && !Outlet) ? (
          <Inactive />
        ) : state.kind === 'unavailable' ? (
          <div className="sign-in__form">
            <PageHeader title={t('links.title')} />
            <p role="alert">{t('links.unavailable')}</p>
          </div>
        ) : state.kind === 'verify' && secret ? (
          <>
            <PageHeader title={t('links.title')} />
            <VerifyStep
              linkId={linkId}
              secret={secret}
              maskedEmail={state.maskedEmail}
              codeSent={state.codeSent}
              onVerified={() =>
                void ready().catch(() => {
                  setState({ kind: 'inactive' });
                })
              }
              onRefused={() => {
                forgetSecret(linkId);
                setState({ kind: 'inactive' });
              }}
            />
          </>
        ) : state.kind === 'ready' && Outlet ? (
          <Suspense
            fallback={
              <p className="sign-in__status" role="status">
                {t('links.opening')}
              </p>
            }
          >
            <Outlet linkId={linkId} session={state.session} onDone={done} />
          </Suspense>
        ) : state.kind === 'done' ? (
          <div className="sign-in__form">
            <PageHeader title={t('links.done.title')} description={t('links.done.body')} />
          </div>
        ) : null}
        <ReportLink linkId={linkId} />
      </div>
    </AuthLayout>
  );
}

/** The one neutral state for every link that does not open, whatever the reason. */
function Inactive() {
  const { t } = useTranslation();
  return (
    <div className="sign-in__form" data-testid="link-inactive">
      <PageHeader title={t('links.inactive.title')} description={t('links.inactive.body')} />
    </div>
  );
}

/** The code step of a link bound to an address. */
function VerifyStep({
  linkId,
  secret,
  maskedEmail,
  codeSent,
  onVerified,
  onRefused,
}: {
  linkId: string;
  secret: string;
  maskedEmail: string;
  codeSent: boolean;
  onVerified: () => void;
  onRefused: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const [sent, setSent] = useState(codeSent);
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [wait, setWait] = useState(codeSent ? 60 : 0);

  useEffect(() => {
    heading.current?.focus();
  }, [sent]);

  useEffect(() => {
    if (wait <= 0) return;
    const timer = window.setTimeout(() => {
      setWait((left) => left - 1);
    }, 1_000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [wait]);

  const failed = (failure: unknown) => {
    if (failure instanceof ApiError && failure.code === 'invalid_link') {
      onRefused();
      return;
    }
    const known = ['incorrect_code', 'expired_code', 'locked', 'no_code', 'rate_limited'];
    const reason =
      failure instanceof ApiError && known.includes(failure.code) ? failure.code : 'generic';
    setProblem(t(`links.verify.errors.${reason as 'generic'}`));
  };

  const send = async () => {
    setProblem(null);
    setPending(true);
    try {
      const result = await sendLinkCode(linkId, secret);
      setSent(true);
      setCode('');
      setWait(result.resendInSeconds);
    } catch (failure) {
      failed(failure);
    } finally {
      setPending(false);
    }
  };

  const submit = async (value: string) => {
    setProblem(null);
    setPending(true);
    try {
      await verifyLink(linkId, secret, value);
      onVerified();
    } catch (failure) {
      setCode('');
      failed(failure);
    } finally {
      setPending(false);
    }
  };

  const labelId = `${id}-label`;
  const messageId = `${id}-message`;
  if (!sent) {
    return (
      <div className="sign-in__form">
        <h2 className="sign-in__step-title" tabIndex={-1} ref={heading}>
          {t('links.verify.title')}
        </h2>
        <p>{t('links.verify.intro', { email: maskedEmail })}</p>
        {problem ? (
          <p className="field__error" role="alert">
            {problem}
          </p>
        ) : null}
        <button
          className="btn btn--primary sign-in__submit"
          type="button"
          disabled={pending}
          onClick={() => void send()}
        >
          {t('links.verify.send')}
        </button>
      </div>
    );
  }
  return (
    <div className="sign-in__form">
      <h2 className="sign-in__step-title" tabIndex={-1} ref={heading}>
        {t('links.verify.enterTitle')}
      </h2>
      <p>{t('links.verify.sent', { email: maskedEmail })}</p>
      <p className="field__label" id={labelId}>
        {t('links.verify.label')}
      </p>
      <CodeEntry
        value={code}
        onChange={setCode}
        onComplete={(value) => void submit(value)}
        labelledBy={labelId}
        {...(problem ? { describedBy: messageId } : {})}
        invalid={Boolean(problem)}
        disabled={pending}
      />
      <p className="sign-in__status" aria-live="polite">
        {pending ? t('links.verify.checking') : ''}
      </p>
      {problem ? (
        <p className="field__error" id={messageId} role="alert">
          {problem}
        </p>
      ) : null}
      <div className="sign-in__actions">
        <NoCodeHelp wait={wait} pending={pending} onResend={() => void send()} />
      </div>
    </div>
  );
}

const REASONS: readonly LinkReportReason[] = ['NOT_FOR_ME', 'SUSPICIOUS', 'UNWANTED', 'OTHER'];

/** "Report this link": always offered, even for a link that does not open. */
function ReportLink({ linkId }: { linkId: string }) {
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<LinkReportReason>('NOT_FOR_ME');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setStatus('sending');
    try {
      await reportLink(linkId, reason, note.trim());
      setStatus('sent');
    } catch {
      setStatus('failed');
    }
  };

  if (status === 'sent') {
    return (
      <p className="secure-link__report-done" role="status">
        {t('links.report.thanks')}
      </p>
    );
  }
  if (!open) {
    return (
      <button
        className="btn btn--quiet secure-link__report-toggle"
        type="button"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('links.report.action')}
      </button>
    );
  }
  return (
    <form
      className="secure-link__report"
      aria-labelledby={`${id}-title`}
      onSubmit={(event) => void submit(event)}
    >
      <h2 className="secure-link__report-title" id={`${id}-title`}>
        {t('links.report.title')}
      </h2>
      <fieldset className="secure-link__reasons">
        <legend className="field__label">{t('links.report.reasonLabel')}</legend>
        {REASONS.map((value) => (
          <label className="secure-link__reason" key={value}>
            <input
              type="radio"
              name={`${id}-reason`}
              value={value}
              checked={reason === value}
              onChange={() => {
                setReason(value);
              }}
            />
            <span>{t(`links.report.reasons.${value}`)}</span>
          </label>
        ))}
      </fieldset>
      <label className="field" htmlFor={`${id}-note`}>
        <span className="field__label">{t('links.report.noteLabel')}</span>
        <textarea
          className="input secure-link__note"
          id={`${id}-note`}
          maxLength={500}
          rows={3}
          value={note}
          onChange={(event) => {
            setNote(event.target.value);
          }}
        />
      </label>
      {status === 'failed' ? (
        <p className="field__error" role="alert">
          {t('links.report.failed')}
        </p>
      ) : null}
      <div className="sign-in__actions">
        <button className="btn btn--secondary" type="submit" disabled={status === 'sending'}>
          {t('links.report.send')}
        </button>
        <button
          className="btn btn--quiet"
          type="button"
          onClick={() => {
            setOpen(false);
          }}
        >
          {t('links.report.cancel')}
        </button>
      </div>
    </form>
  );
}
